import { create } from 'zustand';
import { nekoPos } from 'public-contracts';
import { authenticatedFetch } from '../utils/authenticatedFetch';

interface TableStatusState {
  tableStatuses: nekoPos.TableStatus[];
  isLoading: boolean;
  isFetchingOccupiedTables: boolean;
  error: string | null;
  fetchTableStatuses: () => Promise<void>;
  fetchOccupiedTables: () => Promise<
    Array<{ id: string; tableNumber: number; orderId?: string }>
  >;
  updateTableStatus: (
    tableNumber: number,
    orderId?: string,
    isCheckout?: boolean
  ) => Promise<void>;
  releaseTable: (tableNumber: number) => Promise<void>;
  occupyTable: (tableNumber: number, orderId: string) => Promise<void>;
  clearError: () => void;
}

export const useTableStatusStore = create<TableStatusState>((set, get) => ({
  tableStatuses: [],
  isLoading: false,
  isFetchingOccupiedTables: false,
  error: null,

  fetchTableStatuses: async () => {
    try {
      set({ isLoading: true, error: null });

      // Call the actual API endpoint
      // Call the actual API endpoint
      const response = await authenticatedFetch('/api/table_statuses', {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch table statuses: ${response.statusText}`
        );
      }

      const data = await response.json();
      // Handle both array response and { data: [...] } response format
      const tableStatusesArray = Array.isArray(data) ? data : data.data || [];
      console.log(
        '[TableStatusStore] API response type:',
        Array.isArray(data) ? 'array' : 'object'
      );
      console.log('[TableStatusStore] API response:', data);
      console.log(
        '[TableStatusStore] Parsed table statuses count:',
        tableStatusesArray.length
      );
      console.log(
        '[TableStatusStore] Sample table status:',
        tableStatusesArray[0]
      );
      set({
        tableStatuses: tableStatusesArray,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching table statuses:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch table statuses',
        isLoading: false,
      });
    }
  },

  fetchOccupiedTables: async () => {
    try {
      set({ isFetchingOccupiedTables: true, error: null });

      // Call the actual API endpoint to get all table statuses
      const response = await authenticatedFetch('/api/table_statuses', {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch table statuses: ${response.statusText}`
        );
      }

      const data = await response.json();
      // Handle both array response and { data: [...] } response format
      const tableStatusesArray = Array.isArray(data) ? data : data.data || [];

      console.log(
        '[TableStatusStore] fetchOccupiedTables - API response:',
        data
      );
      console.log(
        '[TableStatusStore] fetchOccupiedTables - Parsed array:',
        tableStatusesArray
      );

      // Filter only occupied tables (has orderId or isAvailable === false)
      const occupiedTables = tableStatusesArray
        .filter(
          (status: nekoPos.TableStatus) =>
            status.orderId || status.isAvailable === false
        )
        .map((status: nekoPos.TableStatus) => ({
          id: status.id,
          tableNumber: status.tableNumber,
          orderId: status.orderId,
        }));

      console.log(
        '[TableStatusStore] fetchOccupiedTables - Occupied tables:',
        occupiedTables
      );
      set({ isFetchingOccupiedTables: false });
      return occupiedTables;
    } catch (error) {
      console.error('Error fetching occupied tables:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch occupied tables',
        isFetchingOccupiedTables: false,
      });
      return [];
    }
  },

  updateTableStatus: async (
    tableNumber: number,
    orderId?: string,
    isCheckout?: boolean
  ) => {
    try {
      set({ isLoading: true, error: null });

      // Call the actual API endpoint
      // Call the actual API endpoint
      const response = await authenticatedFetch('/api/table_statuses', {
        method: 'POST',
        body: JSON.stringify({
          tableNumber,
          orderId,
          isAvailable: isCheckout || !orderId,
          lastOrderDate: orderId ? new Date().toISOString() : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to update table status: ${response.statusText}`
        );
      }

      set({ isLoading: false });
    } catch (error) {
      console.error('Error updating table status:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Failed to update table status',
        isLoading: false,
      });
    }
  },

  releaseTable: async (tableNumber: number) => {
    await get().updateTableStatus(tableNumber, undefined, true);
  },

  occupyTable: async (tableNumber: number, orderId: string) => {
    await get().updateTableStatus(tableNumber, orderId);
  },

  clearError: () => {
    set({ error: null });
  },
}));
